/**
 * AlertBadge — badge numérico de alertas não reconhecidos para o header.
 *
 * Usa @tanstack/react-query com polling a cada 30s para buscar a contagem
 * de alertas não reconhecidos via GET /bff/alerts/count.
 * Também escuta eventos WebSocket (custom event 'm12-alert-ws' no window)
 * para invalidação imediata do cache.
 *
 * Exibição:
 * - Se count === 0: retorna null (badge oculto)
 * - Se count > 99: exibe "99+"
 * - Caso contrário: exibe o número
 *
 * Requirements: 10.6
 */

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface AlertCountResponse {
  data: {
    unacknowledged: number;
  };
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const badgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: '-4px',
  right: '-4px',
  minWidth: '18px',
  height: '18px',
  backgroundColor: '#f04040',
  color: '#ffffff',
  fontSize: '10px',
  fontWeight: 700,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  lineHeight: 1,
  pointerEvents: 'none',
};

// ─── Fetch ─────────────────────────────────────────────────────────────────────

async function fetchAlertCount(): Promise<AlertCountResponse> {
  const res = await fetch('/bff/alerts/count');
  if (!res.ok) {
    return { data: { unacknowledged: 0 } };
  }
  return (await res.json()) as AlertCountResponse;
}

// ─── Component ─────────────────────────────────────────────────────────────────

/**
 * AlertBadge — componente de badge para exibição no header do portal.
 *
 * Deve ser posicionado dentro de um container com `position: relative`
 * (ex: o botão de notificação no AppShell).
 *
 * O badge faz polling a cada 30s e se invalida imediatamente ao receber
 * eventos WebSocket do tipo 'm12-alert-ws' disparados pelo useWsQueryUpdater
 * ou por qualquer outra fonte que emita esse CustomEvent no window.
 */
export function AlertBadge() {
  const queryClient = useQueryClient();

  const { data } = useQuery<AlertCountResponse>({
    queryKey: ['m12-alerts', 'count'],
    queryFn: fetchAlertCount,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  // WebSocket invalidation via custom event no window
  useEffect(() => {
    const handler = () => {
      void queryClient.invalidateQueries({ queryKey: ['m12-alerts', 'count'] });
    };
    window.addEventListener('m12-alert-ws', handler);
    return () => {
      window.removeEventListener('m12-alert-ws', handler);
    };
  }, [queryClient]);

  const count = data?.data.unacknowledged ?? 0;

  if (count === 0) return null;

  const displayText = count > 99 ? '99+' : String(count);

  return (
    <span
      style={badgeStyle}
      aria-label={`${count} alertas não reconhecidos`}
      data-testid="alert-badge-count"
    >
      {displayText}
    </span>
  );
}

export default AlertBadge;
